import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserAuth } from '../contexts/UserAuthContext';

function AuthScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, requestMagicLink, verifyMagicLink, verifyOTP, loginWithGoogle } = useUserAuth();

  const [email, setEmail] = useState('');
  const [step, setStep] = useState('initial'); // 'initial', 'email-sent', 'verifying', 'error'
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // OTP state
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleTokenVerification = useCallback(async (token) => {
    setStep('verifying');
    const result = await verifyMagicLink(token);
    if (result.success) {
      navigate('/');
    } else {
      setStep('error');
      setError(result.error || 'Enlace inválido o expirado');
    }
  }, [verifyMagicLink, navigate]);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleTokenVerification(token);
    }
  }, [searchParams, handleTokenVerification]);

  const handleRequestMagicLink = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Ingresa un email válido');
      return;
    }

    setIsLoading(true);
    setError('');

    const result = await requestMagicLink(email);

    if (result.success) {
      setStep('email-sent');
      setOtpDigits(['', '', '', '', '', '']);
      setOtpError('');
      setTimeout(() => {
        if (otpRefs.current[0]) otpRefs.current[0].focus();
      }, 300);
    } else {
      setError(result.error || 'Error enviando el código');
    }

    setIsLoading(false);
  };

  // OTP input handlers
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...otpDigits];
    newDigits[index] = value.slice(-1);
    setOtpDigits(newDigits);
    setOtpError('');

    // Auto-advance to next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are filled
    const code = newDigits.join('');
    if (code.length === 6 && newDigits.every(d => d !== '')) {
      handleVerifyOTP(code);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;

    const newDigits = [...otpDigits];
    for (let i = 0; i < pasted.length && i < 6; i++) {
      newDigits[i] = pasted[i];
    }
    setOtpDigits(newDigits);

    const focusIndex = Math.min(pasted.length, 5);
    otpRefs.current[focusIndex]?.focus();

    if (pasted.length === 6) {
      handleVerifyOTP(pasted);
    }
  };

  const handleVerifyOTP = async (code) => {
    if (isVerifyingOTP) return;
    setIsVerifyingOTP(true);
    setOtpError('');

    const result = await verifyOTP(email, code);

    if (result.success) {
      navigate('/');
    } else {
      setOtpError(result.error || 'Código incorrecto');
      setOtpDigits(['', '', '', '', '', '']);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }

    setIsVerifyingOTP(false);
  };

  const handleGoogleLogin = useCallback(async (response) => {
    setIsLoading(true);
    setError('');

    const result = await loginWithGoogle(response.credential);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error || 'Error con Google. Intenta de nuevo.');
    }

    setIsLoading(false);
  }, [loginWithGoogle, navigate]);

  useEffect(() => {
    if (step !== 'initial' && step !== 'error') return;

    const initGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleLogin,
        });

        const container = document.getElementById('google-signin-btn');
        if (container) {
          window.google.accounts.id.renderButton(container, {
            theme: 'outline',
            size: 'large',
            width: 280,
            text: 'continue_with',
            shape: 'pill',
            locale: 'es',
          });
        }
      }
    };

    if (window.google?.accounts?.id) {
      initGoogle();
    } else {
      const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = initGoogle;
        document.body.appendChild(script);
      } else {
        existingScript.addEventListener('load', initGoogle);
        if (window.google?.accounts?.id) initGoogle();
      }
    }
  }, [step, handleGoogleLogin]);

  // Verifying state (magic link from URL)
  if (step === 'verifying') {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-verifying">
            <div className="loading-spinner-large"></div>
            <h2>Verificando tu acceso...</h2>
            <p>Espera un momento</p>
          </div>
        </div>
      </div>
    );
  }

  // Email sent — show OTP input
  if (step === 'email-sent') {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-otp-section">
            <div className="auth-otp-icon">&#128274;</div>
            <h2>Ingresa el código</h2>
            <p>
              Enviamos un código de 6 dígitos a <strong>{email}</strong>
            </p>

            <div className="auth-otp-inputs">
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  className={`auth-otp-digit ${otpError ? 'error' : ''} ${isVerifyingOTP ? 'verifying' : ''}`}
                  disabled={isVerifyingOTP}
                />
              ))}
            </div>

            {isVerifyingOTP && (
              <div className="auth-otp-verifying">
                <span className="load-more-spinner"></span> Verificando...
              </div>
            )}

            {otpError && (
              <div className="auth-error" style={{ marginTop: '12px' }}>
                {otpError}
              </div>
            )}

            <p className="auth-otp-hint">
              También puedes abrir el enlace que te enviamos por email
            </p>

            <div className="auth-otp-actions">
              <button
                className="auth-back-btn"
                onClick={() => { setStep('initial'); setError(''); setOtpError(''); }}
              >
                Usar otro email
              </button>
              <button
                className="auth-back-btn"
                onClick={handleRequestMagicLink}
                disabled={isLoading}
              >
                Reenviar código
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Initial / error state
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Inicia sesión o crea tu cuenta</h2>
          <p>Accede para enviar pedidos de oración de forma más rápida</p>
        </div>

        <div className="auth-google-section">
          <div id="google-signin-btn" className="auth-google-btn-wrapper"></div>
        </div>

        <div className="auth-divider">
          <span>o</span>
        </div>

        <form onSubmit={handleRequestMagicLink} className="auth-email-form">
          <label className="auth-label" htmlFor="auth-email">
            Continuar con email
          </label>
          <div className="auth-input-group">
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu.email@ejemplo.com"
              className="auth-input"
              autoComplete="email"
              disabled={isLoading}
            />
            <button
              type="submit"
              className={`auth-submit-btn ${isLoading ? 'loading' : ''}`}
              disabled={isLoading || !email.includes('@')}
            >
              {isLoading ? (
                <span className="load-more-spinner"></span>
              ) : (
                'Enviar código'
              )}
            </button>
          </div>
          <p className="auth-email-hint">
            Te enviaremos un código de 6 dígitos para acceder sin contraseña
          </p>
        </form>

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        <div className="auth-footer">
          <button className="auth-skip-btn" onClick={() => navigate('/')}>
            Continuar sin cuenta
          </button>
        </div>
      </div>
    </div>
  );
}

export default AuthScreen;
